import { useRef, useState, useEffect, RefObject } from 'react';

interface ScrollAnimationOptions {
    /** Percentage of element that must be visible (0-1). Default: 0.3 */
    threshold?: number;
    /** Margin around the root. Default: '0px 0px -100px 0px' */
    rootMargin?: string;
    /** Only trigger once. Default: true */
    triggerOnce?: boolean;
}

interface ScrollAnimationResult<T extends HTMLElement> {
    ref: RefObject<T>;
    isInView: boolean;
}

/**
 * Hook for triggering animations when elements scroll into view.
 * Uses Intersection Observer for performance (no scroll event listeners).
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { ref, isInView } = useScrollAnimation<HTMLDivElement>();
 *   return (
 *     <div ref={ref} className={isInView ? 'animate-fadeIn' : 'opacity-0'}>
 *       Content
 *     </div>
 *   );
 * }
 * ```
 */
export function useScrollAnimation<T extends HTMLElement = HTMLDivElement>(
    options: ScrollAnimationOptions = {}
): ScrollAnimationResult<T> {
    const {
        threshold = 0.3,
        rootMargin = '0px 0px -100px 0px',
        triggerOnce = true
    } = options;

    const ref = useRef<T>(null);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        if (triggerOnce) {
                            observer.unobserve(element);
                        }
                    } else if (!triggerOnce) {
                        setIsInView(false);
                    }
                });
            },
            { threshold, rootMargin }
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [threshold, rootMargin, triggerOnce]);

    return { ref, isInView };
}

export default useScrollAnimation;
